import React from 'react';
import type { WizardStepComponentProps } from 'src/shared/forms/types';
import type { ProductWizardData } from '../../../hooks/useProductWizard';
import { getCategoryIdFromPath } from '../../../../category/utils/categoryTreeBuilder';

interface ReviewStepProps extends WizardStepComponentProps {
  data: ProductWizardData;
  onEditStep: (stepIndex: number) => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  allData,
  onEditStep,
  errors = []
}) => {

  const data = allData as ProductWizardData;

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm" data-testid="wizard-step-review">
      <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900">
          Review & Publish
        </h3>
        <p className="tw-text-sm tw-text-gray-500">
          Review your product information before publishing to the marketplace.
        </p>
      </div>
      <div className="tw-grid tw-gap-6">
        {/* Basic Information Summary */}
        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4" data-testid="wizard-review-basicInfo-card">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <h4 className="tw-font-semibold tw-text-gray-900">Basic Information</h4>
            <button 
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => onEditStep(0)}
            >
              Edit
            </button>
          </div>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-text-sm">
            <div>
              <span className="tw-text-gray-500">Store:</span>
              <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                {data.basicInfo?.selectedStore || 'Not set'}
              </span>
            </div>
            <div>
              <span className="tw-text-gray-500">Product Name:</span>
              <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                {data.basicInfo?.name || 'Not set'}
              </span>
            </div>
            <div>
              <span className="tw-text-gray-500">Master SKU:</span>
              <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                {data.basicInfo?.masterSku || 'Not set'}
              </span>
            </div>
            {data.basicInfo?.videoUrl && (
              <div className="tw-col-span-1 md:tw-col-span-2">
                <span className="tw-text-gray-500">Video URL:</span>
                <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                  {data.basicInfo.videoUrl}
                </span>
              </div>
            )}
          </div>
          <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500">Description:</span>
              <p className="tw-text-gray-900 tw-mt-1 tw-text-xs tw-leading-relaxed">
                {data.basicInfo?.description || 'No description provided'}
              </p>
            </div>
          </div>
        </div>

        {/* Product Attributes Summary */}
        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4" data-testid="wizard-review-attributes-card">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <h4 className="tw-font-semibold tw-text-gray-900">Product Attributes</h4>
            <button 
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => onEditStep(1)}
            >
              Edit
            </button>
          </div>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-text-sm">
            <div>
              <span className="tw-text-gray-500">Category:</span>
              <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                {getCategoryIdFromPath(data.category?.initialCategoryPath || []) || 'Not set'}
              </span>
            </div>
            <div>
              <span className="tw-text-gray-500">Brand:</span>
              <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                {data.category?.brandId || 'Not set'}
              </span>
            </div>
          </div>
        </div>

        {/* Sales Information Summary */}
        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4" data-testid="wizard-review-salesInfo-card">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <h4 className="tw-font-semibold tw-text-gray-900">Sales Information</h4>
            <button 
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => onEditStep(2)}
            >
              Edit
            </button>
          </div>
          <div className="tw-text-sm tw-space-y-3">
            {/* Minimum Purchase */}
            <div className="tw-pb-3 tw-border-b tw-border-gray-100">
              <span className="tw-text-gray-500">Minimum Purchase:</span>
              <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                {data.salesInfo?.minPurchaseQuantity || 1} unit{(data.salesInfo?.minPurchaseQuantity || 1) !== 1 ? 's' : ''}
              </span>
            </div>
            
            {!data.salesInfo?.hasVariants && (
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                <div>
                  <span className="tw-text-gray-500">Base Price:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.salesInfo?.basePrice ? `Rp ${data.salesInfo.basePrice.toLocaleString('id-ID')}` : 'Not set'}
                  </span>
                </div>
                {data.salesInfo?.comparePrice && (
                  <div>
                    <span className="tw-text-gray-500">Compare Price:</span>
                    <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                      Rp {data.salesInfo.comparePrice.toLocaleString('id-ID')}
                    </span>
                  </div>
                )}
                <div>
                  <span className="tw-text-gray-500">Stock:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.salesInfo?.inventory?.quantity || 'Not set'}
                  </span>
                </div>
              </div>
            )}
            
            {data.salesInfo?.hasVariants && (
              <div>
                <div className="tw-mb-2">
                  <span className="tw-text-gray-500">Has Variants:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">Yes</span>
                </div>
                <div className="tw-mb-3">
                  <span className="tw-text-gray-500">Variant Options:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.salesInfo.variantOptions?.length || 0} options configured
                  </span>
                </div>
                <div>
                  <span className="tw-text-gray-500">Variant Combinations:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.salesInfo.variants?.length || 0} combinations configured
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Shipping Information Summary */}
        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4" data-testid="wizard-review-shippingInfo-card">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <h4 className="tw-font-semibold tw-text-gray-900">Shipping Information</h4>
            <button 
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => onEditStep(3)}
            >
              Edit
            </button>
          </div>
          <div className="tw-text-sm tw-space-y-4">
            {/* Package Dimensions */}
            <div className="tw-pb-3 tw-border-b tw-border-gray-100">
              <h5 className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-2">Package Dimensions</h5>
              <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                <div>
                  <span className="tw-text-gray-500">Weight:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.shipping?.dimensions?.weight || 0}{data.shipping?.dimensions?.weightUnit || 'kg'}
                  </span>
                </div>
                <div>
                  <span className="tw-text-gray-500">Length:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.shipping?.dimensions?.length || 0}cm
                  </span>
                </div>
                <div>
                  <span className="tw-text-gray-500">Width:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.shipping?.dimensions?.width || 0}cm
                  </span>
                </div>
                <div>
                  <span className="tw-text-gray-500">Height:</span>
                  <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                    {data.shipping?.dimensions?.height || 0}cm
                  </span>
                </div>
              </div>
              <div className="tw-mt-2">
                <span className="tw-text-gray-500">Dangerous Item:</span>
                <span className="tw-text-gray-900 tw-font-medium tw-ml-2">
                  {data.shipping?.isDangerousProduct ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            
            {/* Selected Shipping Shippers */}
            <div>
              <h5 className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-2">Selected Shipping Shippers & Services</h5>
              <div className="tw-space-y-2">
                {data.shipping?.shippers && Object.entries(data.shipping.shippers)
                  .filter(([, shipper]) => shipper.enabled)
                  .map(([shipperId, shipper]) => (
                    <div key={shipperId} className="tw-flex tw-items-start tw-gap-2">
                      <span className="material-symbols-outlined tw-text-red-600 tw-text-sm">{shipper.icon || 'local_shipping'}</span>
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-text-gray-900 tw-font-medium">{shipper.name}</span>
                        {shipper.children && shipper.children.length > 0 && (
                          <span className="tw-text-xs tw-text-gray-500 tw-mt-1">
                            Services: {shipper.children.filter(c => c.enabled).map(c => c.name).join(', ') || 'No services selected'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                {(!data.shipping?.shippers || Object.values(data.shipping.shippers).every(shipper => !shipper.enabled)) && (
                  <div className="tw-text-xs tw-text-gray-500">
                    No shipping shippers selected
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pre-Order Information Summary */}
        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4" data-testid="wizard-review-preorderInfo-card">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <h4 className="tw-font-semibold tw-text-gray-900">Pre-Order Settings</h4>
            <button 
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => onEditStep(3)}
            >
              Edit
            </button>
          </div>
          <div className="tw-text-sm tw-space-y-3">
            {/* Pre-Order Status */}
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-gray-500">Status:</span>
              <div className="tw-flex tw-items-center tw-gap-2">
                {data.shipping?.preOrder?.enabled ? (
                  <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-blue-100 tw-text-blue-800">
                    <span className="material-symbols-outlined tw-text-sm tw-mr-1">schedule</span>
                    Enabled
                  </span>
                ) : (
                  <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-700">
                    <span className="material-symbols-outlined tw-text-sm tw-mr-1">block</span>
                    Disabled
                  </span>
                )}
              </div>
            </div>
            
            {/* Pre-Order Details (when enabled) */}
            {data.shipping?.preOrder?.enabled && (
              <div className="tw-space-y-2 tw-pt-2 tw-border-t tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-gray-500">Ship in Days:</span>
                  <span className="tw-text-gray-900 tw-font-medium">
                    {data.shipping.preOrder.shipInDays} days
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-gray-500">Order Type:</span>
                  <span className="tw-text-gray-900 tw-font-medium">
                    {data.shipping.preOrder.type}
                  </span>
                </div>
              </div>
            )}
            
            {/* When disabled */}
            {!data.shipping?.preOrder?.enabled && (
              <div className="tw-text-xs tw-text-gray-500 tw-pt-1">
                Product will be shipped immediately when available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;